import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { 
  Truck, Check, ArrowRight, Battery, Clock, MapPin, 
  BarChart3, Shield, Zap, Users
} from 'lucide-react';

const FleetPage = () => {
  return (
    <div data-testid="fleet-page" className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden" style={{ background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-lime-400 rounded-full blur-3xl" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-lime-400 rounded-full px-4 py-2 mb-6">
              <Truck className="w-4 h-4 text-slate-900" />
              <span className="text-sm font-semibold text-slate-900">Zgjidhje për Flota</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white font-['Inter_Tight'] tracking-tight mb-6">
              Menaxhim i Flotës së Automjeteve Elektrike
            </h1>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              Optimizoni operacionet e flotës suaj me zgjidhjet e centralizuara të karikimit nga AlbMove. 
              Reduktoni kostot, rrisni efikasitetin dhe monitoroni gjithçka në kohë reale.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Button 
                asChild
                className="bg-lime-400 hover:bg-lime-300 text-slate-900 rounded-full px-8 py-6 text-lg font-semibold"
              >
                <Link to="/contact">
                  Kërkoni Ofertë
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Benefits Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Inter_Tight'] mb-6">
                Pse të Zgjidhni AlbMove për Flotën Tuaj?
              </h2>
              <p className="text-slate-500 mb-8 leading-relaxed">
                Me AlbMove, flotës suaj i siguroni infrastrukturën më të avancuar të karikimit në Shqipëri. 
                Menaxhoni të gjitha automjetet nga një panel i vetëm kontrolli.
              </p>
              
              <ul className="space-y-4">
                {[
                  'Monitorim në kohë reale i të gjithë automjeteve',
                  'Raporte të detajuara të konsumit dhe kostove',
                  'Planifikim i optimizuar i karikimit',
                  'Reduktim i kostove operative deri në 40%',
                  'Suport teknik i dedikuar 24/7'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-lime-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-slate-900" />
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1761491713025-a7e66bf9427e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHx3aGl0ZSUyMGZsZWV0JTIwY2FycyUyMHBhcmtpbmclMjBsb3QlMjBidXNpbmVzc3xlbnwwfHx8fDE3NzA5NDcyMDJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Flotë automjetesh elektrike"
                className="rounded-3xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-blue-600 text-white rounded-2xl p-5 shadow-lg">
                <p className="text-3xl font-bold font-['Inter_Tight']">-40%</p>
                <p className="text-blue-200 text-sm">Kosto Operative</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Features Grid */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Inter_Tight'] mb-4">
              Veçoritë e Platformës
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: BarChart3, title: 'Dashboard Real-time', desc: 'Monitoroni statusin e çdo automjeti' },
              { icon: Battery, title: 'Smart Charging', desc: 'Karikimi i optimizuar sipas tarifave' },
              { icon: MapPin, title: 'Gjurmim GPS', desc: 'Vendndodhja e çdo automjeti' },
              { icon: Shield, title: 'Siguri e Plotë', desc: 'Enkriptim dhe autentikim 2FA' }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 border border-slate-200/50">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="rounded-3xl p-12 text-center" style={{ background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)' }}>
            <h2 className="text-3xl md:text-4xl font-bold text-white font-['Inter_Tight'] mb-4">
              Gati të Optimizoni Flotën Tuaj?
            </h2>
            <p className="text-blue-100 mb-8 max-w-xl mx-auto">
              Kontaktoni ekipin tonë për një konsultim falas dhe ofertë të personalizuar.
            </p>
            <Button 
              asChild
              className="bg-lime-400 hover:bg-lime-300 text-slate-900 rounded-full px-8 py-6 text-lg font-semibold"
            >
              <Link to="/contact">
                Na Kontaktoni Tani
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FleetPage;
